import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationsApi } from '../../services/apiService';
import { useSocket } from '../../context/SocketContext';
import './MobileNotificationsPage.css';

const DUMMY_NOTIFICATIONS = [
  {
    id: 'demo1',
    title: '1 new comment: This supplier is amazing',
    message: 'mobile gaming finger sleeve: Source from popular suppliers >>',
    image: null,
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    isRead: false,
    type: 'promotion',
    category: 'Promotions',
  },
  {
    id: 'demo2',
    title: '✦ Find suppliers with AI tools',
    message: 'Hi, discover how AI can transform your sourcing',
    image: null,
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    isRead: false,
    type: 'promotion',
    category: 'Promotions',
  },
  {
    id: 'demo3',
    title: '✔ Worry-free delivery',
    message: 'Hi, these products can ship in 72 hours',
    image: null,
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    isRead: true,
    type: 'promotion',
    category: 'Promotions',
  },
];

const formatNotifDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return 'Today';
  if (diff < 172800000) return 'Yesterday';
  const days = Math.floor(diff / 86400000);
  if (days < 7) return `${days} days ago`;
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  return weekday;
};

const groupByDate = (notifications) => {
  const groups = {};
  notifications.forEach(n => {
    const label = formatNotifDate(n.createdAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(n);
  });
  return groups;
};

const MobileNotificationsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { on, off } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [useDummy, setUseDummy] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) { setLoading(false); setUseDummy(true); return; }
    try {
      setLoading(true);
      const data = await notificationsApi.getMy();
      const items = data.notifications || [];
      if (items.length === 0) {
        setNotifications(DUMMY_NOTIFICATIONS);
        setUseDummy(true);
      } else {
        setNotifications(items);
        setUseDummy(false);
      }
    } catch (_) {
      setNotifications(DUMMY_NOTIFICATIONS);
      setUseDummy(true);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Real-time notifications
  useEffect(() => {
    if (!on || !off || useDummy) return;
    const handler = (data) => {
      setNotifications(prev => [{ ...data, isRead: false }, ...prev]);
    };
    on('notification', handler);
    return () => off('notification', handler);
  }, [on, off, useDummy]);

  const handleMarkRead = async (id) => {
    if (useDummy) {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      return;
    }
    try {
      await notificationsApi.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch (_) {}
  };

  const handleMarkAllRead = async () => {
    if (useDummy) {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      return;
    }
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (_) {}
  };

  const grouped = groupByDate(notifications);
  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="mn-page">
      {/* Header */}
      <div className="mn-header">
        <button className="mn-back-btn" onClick={() => navigate('/mobile/messenger')} aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mn-title">Notifications</h1>
        <div className="mn-header-actions">
          <button className="mn-icon-btn" aria-label="Settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
          </button>
          <button className="mn-icon-btn" onClick={handleMarkAllRead} aria-label="Delete all / mark read">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Push notification notice */}
      {!noticeDismissed && (
        <div className="mn-notice-bar">
          <div className="mn-notice-text">
            <strong>Stay in the know</strong>
            <span>Enable push notifications and never miss out on order updates and promotions. <button className="mn-enable-btn">Enable</button></span>
          </div>
          <button className="mn-notice-close" onClick={() => setNoticeDismissed(true)} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Body */}
      <div className="mn-body">
        {loading ? (
          <div className="mn-loading">
            {[1, 2, 3, 4].map(i => <div key={i} className="mn-skeleton" />)}
          </div>
        ) : notifications.length === 0 ? (
          <div className="mn-empty">
            <div className="mn-empty-illustration">
              <svg viewBox="0 0 120 120" fill="none">
                <circle cx="60" cy="55" r="36" fill="#f5f5f5" />
                <ellipse cx="60" cy="95" rx="30" ry="8" fill="#f0f0f0" />
                <rect x="30" y="42" width="24" height="20" rx="6" fill="#ddd" />
                <rect x="66" y="42" width="24" height="20" rx="6" fill="#ddd" />
                <rect x="48" y="48" width="24" height="8" rx="3" fill="#ccc" />
                <circle cx="42" cy="52" r="10" fill="#e8e8e8" stroke="#ccc" strokeWidth="2" />
                <circle cx="78" cy="52" r="10" fill="#e8e8e8" stroke="#ccc" strokeWidth="2" />
                <circle cx="42" cy="52" r="5" fill="#d0d0d0" />
                <circle cx="78" cy="52" r="5" fill="#d0d0d0" />
                <circle cx="60" cy="78" r="6" fill="#ccc" />
                <path d="M46 95c0-8 6-14 14-14s14 6 14 14" fill="#ddd" />
                <circle cx="28" cy="70" r="8" fill="#ff6a00" opacity="0.7" />
                <ellipse cx="90" cy="68" rx="7" ry="10" fill="#ff6a00" opacity="0.7" />
              </svg>
            </div>
            <p className="mn-empty-label">No Notifications</p>
          </div>
        ) : (
          <div className="mn-list">
            {Object.entries(grouped).map(([dateLabel, items]) => (
              <div key={dateLabel}>
                <div className="mn-date-group">{dateLabel}</div>
                {items.map(notif => (
                  <div
                    key={notif.id}
                    className={`mn-card ${notif.isRead ? 'mn-card--read' : 'mn-card--unread'}`}
                    onClick={() => handleMarkRead(notif.id)}
                  >
                    {/* Unread red dot — leftmost column */}
                    <div className="mn-unread-col">
                      {!notif.isRead && <span className="mn-unread-dot" />}
                    </div>
                    {/* Gray avatar circle */}
                    <div className="mn-avatar-circle">
                      {notif.avatar ? (
                        <img src={notif.avatar} alt="" />
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 01-3.46 0" />
                        </svg>
                      )}
                    </div>
                    {/* Content */}
                    <div className="mn-card-body">
                      <div className="mn-card-top">
                        <span className="mn-card-category">{notif.category || notif.type || 'Promotions'}</span>
                        <span className="mn-card-time">{formatNotifDate(notif.createdAt)}</span>
                      </div>
                      <p className="mn-card-title">{notif.title}</p>
                      <p className="mn-card-msg">{notif.message || notif.body}</p>
                      {notif.image && (
                        <div className="mn-card-img-wrap">
                          <img src={notif.image} alt="" className="mn-card-img" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileNotificationsPage;
